<template>
  <div class="otp-wrapper position-relative" :class="{ 'opacity-50 pe-none': disabled }" role="group" :aria-label="ariaLabel">
    <!-- Real input (single field) -->
    <input
      ref="inputEl"
      :id="id"
      type="text"
      class="position-absolute top-0 start-0 w-100 h-100 opacity-0"
      :value="modelValue"
      :maxlength="length"
      :disabled="disabled"
      :inputmode="numeric ? 'numeric' : 'text'"
      :pattern="numeric ? '[0-9]*' : undefined"
      autocomplete="one-time-code"
      @input="onInput"
      @paste.prevent="onPaste"
      @focus="isFocused = true"
      @blur="isFocused = false"
    />

    <!-- Visual slots -->
    <div class="d-flex gap-2 justify-content-center flex-grow-1 h-100">
      <div
        v-for="i in length"
        :key="i"
        class="otp-box d-flex align-items-center justify-content-center text-center"
        :class="{
          'otp-focus': isFocused && focusIndex === (i - 1),
          'bg-body-secondary': disabled
        }"
        @click="focusAt(i - 1)"
        :aria-label="`Character ${i}`"
      >
        <span class="otp-char">{{ displayChar(i - 1) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";

const props = defineProps<{
  modelValue: string;
  length?: number;          // default 6
  numeric?: boolean;        // default true (digits only)
  masked?: boolean;         // default false (show • instead of digits)
  placeholderChar?: string; // default '–'
  id?: string;
  disabled?: boolean;
  ariaLabel?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "complete", value: string): void;
}>();

const length = computed(() => props.length ?? 6);
const numeric = computed(() => props.numeric ?? true);
const masked = computed(() => props.masked ?? false);
const placeholderChar = computed(() => props.placeholderChar ?? "–");

const inputEl = ref<HTMLInputElement | null>(null);
const isFocused = ref(false);

// Where to show the "focus" highlight: next empty box (or last)
const focusIndex = computed(() =>
  Math.min((props.modelValue?.length ?? 0), length.value - 1)
);

function sanitize(raw: string): string {
  let s = (raw ?? "").toString();
  if (numeric.value) {
    // Convert "1,23" style to "123" digits only
    s = s.replace(/[^\d]/g, "");
  } else {
    s = s.replace(/\s+/g, "");
  }
  return s.slice(0, length.value);
}

function onInput(e: Event) {
  const v = sanitize((e.target as HTMLInputElement).value);
  emit("update:modelValue", v);
  if (v.length === length.value) emit("complete", v);
}

function onPaste(e: ClipboardEvent) {
  const text = e.clipboardData?.getData("text") ?? "";
  const v = sanitize(text);
  emit("update:modelValue", v);
  nextTick(() => inputEl.value?.setSelectionRange(v.length, v.length));
  if (v.length === length.value) emit("complete", v);
}

function displayChar(idx: number) {
  const char = props.modelValue?.[idx] ?? "";
  if (!char) return placeholderChar.value;
  return masked.value ? "•" : char;
}

function focusAt(index: number) {
  inputEl.value?.focus();
  const pos = Math.max(0, Math.min(index, length.value));
  nextTick(() => inputEl.value?.setSelectionRange(pos, pos));
}

onMounted(() => {
  // Ensure the overlay input covers boxes for easy typing
  // (height controlled by CSS below)
});
</script>

<style scoped>
/* Make the visual boxes match Bootstrap form-control size vibe */
.otp-wrapper {
  /* Height should match the boxes so the hidden input covers them */
  height: calc(2.5rem + 2px); /* approx default .form-control height */
}

/* Each box */
.otp-box {
  width: 2.75rem;
  height: 100%;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background-color: var(--bs-body-bg);
  cursor: text;
  user-select: none;
  font-size: 1.25rem;
  line-height: 1;
}

/* Focus highlight for the "active" slot */
.otp-focus {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 .2rem rgba(var(--bs-primary-rgb), .25);
}

/* Placeholder vs char */
.otp-char {
  transform: translateY(1px);
  color: var(--bs-body-color);
  font-variant-numeric: tabular-nums;
  letter-spacing: .02em;
}
</style>
